import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  MessageCircle,
  Search,
  PenLine,
  Rocket,
  RefreshCw,
  Phone,
  Mail,
  Check,
  Sparkles,
  Bot,
  Briefcase,
  LayoutGrid,
} from 'lucide-react';
import SEO from '../components/SEO';
import InternalLinkFooter from '../components/InternalLinkFooter';
import ContinueExploring from '../components/ContinueExploring';
import AIMonitoringStrip from '../components/AIMonitoringStrip';
import { useT } from '../i18n';

/* HowItWorksPage — relationship-first journey: discovery call → understanding
   → tailored proposal → frictionless launch → continuous operation.
   Everything customizable, everything ends in a CTA and links into services. */

const STEPS = [
  {
    icon: MessageCircle,
    en: {
      title: 'A 15-minute discovery call',
      body: 'It starts with a conversation — WhatsApp, phone or video, whatever suits you. You tell us about your operation, your goals and what is hurting right now. No commitment, no sales scripts.',
      chips: ['WhatsApp or call', 'EN / ES', 'Zero pressure'],
    },
    es: {
      title: 'Una llamada de descubrimiento de 15 minutos',
      body: 'Todo empieza con una conversación — WhatsApp, teléfono o video, como prefieras. Nos cuentas tu operación, tus objetivos y qué te está doliendo ahora. Sin compromiso y sin guiones de venta.',
      chips: ['WhatsApp o llamada', 'EN / ES', 'Cero presión'],
    },
  },
  {
    icon: Search,
    en: {
      title: 'We understand your business first',
      body: 'Before proposing anything, we audit your visibility, your tech stack and your numbers — free. We never sell you what you don\'t need: first we find where the real opportunity is.',
      chips: ['Free audit', 'Honest report', 'Your real opportunity'],
    },
    es: {
      title: 'Primero entendemos tu negocio',
      body: 'Antes de proponerte nada, auditamos tu visibilidad, tu stack y tus números — gratis. Nunca te vendemos lo que no necesitas: primero encontramos dónde está la oportunidad real.',
      chips: ['Auditoría gratis', 'Reporte honesto', 'Tu oportunidad real'],
    },
  },
  {
    icon: PenLine,
    en: {
      title: 'A proposal built for you',
      body: 'A clear plan with only what your restaurant needs, transparent pricing from $99/mo and expected outcomes. You decide the scope — a food truck doesn\'t need the same as a 12-location group.',
      chips: ['From $99/mo', 'You choose the scope', 'No rigid bundles'],
    },
    es: {
      title: 'Una propuesta armada para ti',
      body: 'Un plan claro con solo lo que tu restaurante necesita, precio transparente desde $99/mes y resultados esperados. Tú decides el alcance — un food truck no necesita lo mismo que un grupo de 12 sedes.',
      chips: ['Desde $99/mes', 'Tú eliges el alcance', 'Sin paquetes rígidos'],
    },
  },
  {
    icon: Rocket,
    en: {
      title: 'Frictionless launch',
      body: 'We configure everything — POS, Google, website, AI agents, automations. Your team doesn\'t change how it works for a single day. You never touch the technology.',
      chips: ['We set it all up', 'No team disruption', 'Live in weeks'],
    },
    es: {
      title: 'Lanzamiento sin fricción',
      body: 'Configuramos todo nosotros — POS, Google, sitio web, agentes IA, automatizaciones. Tu equipo no cambia su forma de trabajar ni un solo día. Tú nunca tocas la tecnología.',
      chips: ['Lo configuramos todo', 'Sin interrumpir a tu equipo', 'En vivo en semanas'],
    },
  },
  {
    icon: RefreshCw,
    en: {
      title: 'We operate and grow with you',
      body: 'Monitoring, reporting and optimization never stop. Every quarter we review together — and you scale up, adjust or scale down whenever you want. No forced contracts.',
      chips: ['Monthly reporting', 'Quarterly reviews', 'Scale up or down anytime'],
    },
    es: {
      title: 'Operamos y crecemos contigo',
      body: 'El monitoreo, los reportes y la optimización nunca paran. Cada trimestre revisamos juntos — y escalas, ajustas o reduces cuando quieras. Sin contratos forzosos.',
      chips: ['Reporte mensual', 'Revisión trimestral', 'Escala cuando quieras'],
    },
  },
];

const START_POINTS = [
  {
    icon: Sparkles,
    href: '/index-ai',
    en: { t: 'Index AI™', b: 'Be the restaurant ChatGPT, Gemini and Google AI recommend.' },
    es: { t: 'Index AI™', b: 'Sé el restaurante que ChatGPT, Gemini y Google AI recomiendan.' },
    price: lang => (lang === 'es' ? 'Desde $99/mes' : 'From $99/mo'),
  },
  {
    icon: Bot,
    href: '/ai-agents-for-restaurants',
    en: { t: 'AI Workforce™', b: 'Rachel and her team answer calls, reviews and guests 24/7.' },
    es: { t: 'AI Workforce™', b: 'Rachel y su equipo atienden llamadas, reseñas y clientes 24/7.' },
    price: lang => (lang === 'es' ? 'Desde $199/mes' : 'From $199/mo'),
  },
  {
    icon: Briefcase,
    href: '/ai/catering-ai',
    en: { t: 'Catering AI™', b: 'Turn corporate catering into a predictable revenue pipeline.' },
    es: { t: 'Catering AI™', b: 'Convierte el catering corporativo en un pipeline predecible.' },
    price: lang => (lang === 'es' ? 'Desde $399/mes' : 'From $399/mo'),
  },
  {
    icon: LayoutGrid,
    href: '/services',
    en: { t: 'All services', b: 'Websites, Google Business, delivery, data, reputation and more.' },
    es: { t: 'Todos los servicios', b: 'Webs, Google Business, delivery, datos, reputación y más.' },
    price: lang => (lang === 'es' ? 'Desde $99/mes' : 'From $99/mo'),
  },
];

export default function HowItWorksPage() {
  const { lang } = useT();
  const isEs = lang === 'es';

  return (
    <div className="pt-28 pb-20 bg-white">
      <SEO
        title={isEs ? 'Cómo Funciona · Kitxens' : 'How It Works · Kitxens'}
        description={isEs
          ? 'Una llamada de descubrimiento, un diagnóstico honesto, una propuesta a tu medida y un equipo que opera contigo cada mes. Así trabaja Kitxens.'
          : 'A discovery call, an honest diagnosis, a tailored proposal and a team that operates with you every month. This is how Kitxens works.'}
        path="/how-it-works"
      />
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase mb-4">
            {isEs ? 'Cómo Trabajamos' : 'How We Work'}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] text-neutral-900 leading-[1.05]">
            {isEs ? 'Empieza con una llamada. Termina con un socio.' : 'It starts with a call. It ends with a partner.'}
          </h1>
          <p className="mt-6 text-neutral-600 text-lg sm:text-xl leading-relaxed max-w-2xl font-normal">
            {isEs
              ? 'No vendemos software ni paquetes rígidos. Escuchamos tu operación, diseñamos un plan a tu medida y lo operamos contigo — mes a mes, adaptándonos siempre a ti.'
              : "We don't sell software or rigid bundles. We listen to your operation, design a plan around you, and operate it with you — month after month, always adapting to you."}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={`https://wa.me/14085901102?text=${encodeURIComponent(isEs ? 'Hola Kitxens, quiero una llamada de descubrimiento de 15 minutos.' : "Hi Kitxens, I'd like a 15-minute discovery call.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb959] text-white h-12 px-7 rounded-full text-[15px] font-semibold shadow-sm transition-colors"
              data-testid="how-hero-whatsapp"
            >
              <MessageCircle className="w-4 h-4" /> {isEs ? 'Empezar por WhatsApp' : 'Start on WhatsApp'}
            </a>
            <Link to="/#audit" className="inline-flex items-center gap-1 border border-neutral-300 hover:border-[#F77F11] hover:text-[#F77F11] text-neutral-800 h-12 px-7 rounded-full text-[15px] font-semibold transition-colors" data-testid="how-hero-audit">
              {isEs ? 'Pedir auditoría gratis' : 'Get a free audit'} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>

      <div className="mt-14">
        <AIMonitoringStrip />
      </div>

      {/* The journey */}
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <div className="mt-20 relative">
          <div className="hidden md:block absolute left-7 top-2 bottom-2 w-px bg-gradient-to-b from-orange-200 via-orange-100 to-transparent" />
          <div className="space-y-8">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const t = isEs ? s.es : s.en;
              return (
                <motion.div
                  key={t.title}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.45, delay: 0.05 * i }}
                  className="relative flex items-start gap-6 rounded-3xl border border-neutral-200 bg-white p-6 sm:p-8 hover:border-orange-200 hover:shadow-[0_20px_60px_-30px_rgba(247,127,17,0.4)] transition-all"
                  data-testid={`how-step-${i + 1}`}
                >
                  <div className="relative shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F77F11] to-[#ff9b4a] flex items-center justify-center shadow-md">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase">{isEs ? `Paso ${i + 1}` : `Step ${i + 1}`}</p>
                    <h3 className="mt-1 text-2xl sm:text-[28px] font-semibold text-neutral-900 leading-tight tracking-[-0.015em]">{t.title}</h3>
                    <p className="mt-3 text-neutral-600 leading-relaxed">{t.body}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {t.chips.map((chip) => (
                        <span key={chip} className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 border border-orange-100 text-neutral-700 text-[12px] font-semibold px-3 py-1">
                          <Check className="w-3 h-3 text-[#F77F11]" /> {chip}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Adaptability banner */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
          className="mt-16 rounded-3xl bg-neutral-950 text-white p-8 sm:p-12 relative overflow-hidden"
          data-testid="how-adaptable"
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(circle at 22% 28%, #F77F11 1px, transparent 1.6px), radial-gradient(circle at 78% 72%, #F77F11 1px, transparent 1.6px)', backgroundSize: '120px 120px, 170px 170px' }}
          />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase">{isEs ? 'Lo más importante' : 'The most important part'}</p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-[-0.015em] leading-tight">
                {isEs ? 'Todo es personalizable. Todo.' : 'Everything is customizable. Everything.'}
              </h2>
              <p className="mt-5 text-neutral-300 leading-relaxed">
                {isEs
                  ? 'No hay dos restaurantes iguales. Puedes empezar solo con tu Perfil de Google, solo con Rachel contestando el teléfono, o con el programa completo. Cada trimestre ajustamos el plan a cómo evoluciona tu negocio.'
                  : 'No two restaurants are alike. You can start with just your Google Profile, just Rachel answering the phone, or the full program. Every quarter we adjust the plan to how your business evolves.'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {(isEs
                ? [
                    { k: 'Empieza donde duela', v: 'Un servicio, un problema, un resultado — luego escalas.' },
                    { k: 'Sin permanencia', v: 'Mes a mes. Te quedas porque funciona, no por contrato.' },
                    { k: 'Tu presupuesto manda', v: 'Programas desde $99/mes que crecen contigo.' },
                    { k: 'Revisión trimestral', v: 'Ajustamos alcance, prioridades y precio juntos.' },
                  ]
                : [
                    { k: 'Start where it hurts', v: 'One service, one problem, one result — then scale.' },
                    { k: 'No lock-in', v: 'Month to month. You stay because it works, not by contract.' },
                    { k: 'Your budget leads', v: 'Programs from $99/mo that grow with you.' },
                    { k: 'Quarterly review', v: 'We adjust scope, priorities and price together.' },
                  ]
              ).map((s) => (
                <div key={s.k} className="rounded-2xl bg-neutral-900/70 border border-neutral-800 hover:border-[#F77F11] transition-colors p-5">
                  <p className="font-bold text-[15px] text-[#FFB672]">{s.k}</p>
                  <p className="text-[12.5px] text-neutral-400 leading-relaxed mt-1">{s.v}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Value framing */}
        <div className="mt-16 rounded-3xl border border-neutral-200 bg-neutral-50 p-8 sm:p-10 text-center" data-testid="how-value">
          <p className="kx-eyebrow">{isEs ? '¿Y cuánto cuesta?' : 'And how much does it cost?'}</p>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-neutral-900 tracking-tight max-w-2xl mx-auto">
            {isEs
              ? 'Un departamento completo de tecnología, marketing e IA — por menos de lo que cuesta un empleado a medio tiempo.'
              : 'A full technology, marketing and AI department — for less than a part-time employee.'}
          </h2>
          <p className="mt-4 text-neutral-600 max-w-xl mx-auto text-[15px] leading-relaxed">
            {isEs
              ? 'Desde $99/mes. Sin costos de instalación ocultos, sin contratos forzosos, y con resultados que se miden en tu P&L cada mes.'
              : 'From $99/mo. No hidden setup fees, no forced contracts, and results measured in your P&L every month.'}
          </p>
          <Link to="/pricing" className="mt-6 inline-flex items-center gap-1.5 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-7 rounded-full text-[15px] font-semibold transition-colors" data-testid="how-value-pricing">
            {isEs ? 'Ver los programas y precios' : 'See programs & pricing'} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Where to start — linkable to platforms & services */}
        <div className="mt-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="kx-eyebrow mb-4">{isEs ? '¿Por dónde empezar?' : 'Where to start?'}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight">
              {isEs ? 'Elige tu punto de entrada.' : 'Pick your entry point.'}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {START_POINTS.map((p) => {
              const t = isEs ? p.es : p.en;
              const price = typeof p.price === 'function' ? p.price(lang) : p.price;
              return (
                <Link
                  key={p.href}
                  to={p.href}
                  className="group flex flex-col rounded-2xl border border-neutral-200 bg-white p-6 hover:border-[#F77F11] hover:shadow-[0_20px_60px_-30px_rgba(247,127,17,0.4)] transition-all"
                  data-testid={`how-start-${p.href.replace(/[^a-z0-9]+/gi, '-').replace(/(^-|-$)/g, '')}`}
                >
                  <span className="inline-flex w-11 h-11 rounded-xl bg-orange-50 items-center justify-center group-hover:bg-[#F77F11] transition-colors">
                    <p.icon className="w-5 h-5 text-[#F77F11] group-hover:text-white transition-colors" />
                  </span>
                  <h3 className="mt-4 font-bold text-neutral-900 text-[16px]">{t.t}</h3>
                  <p className="mt-1.5 text-[13px] text-neutral-600 leading-relaxed flex-1">{t.b}</p>
                  <span className="mt-4 flex items-center justify-between">
                    <span className="text-[12.5px] font-bold text-[#F77F11]">{price}</span>
                    <ArrowRight className="w-4 h-4 text-neutral-400 group-hover:text-[#F77F11] group-hover:translate-x-1 transition-all" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-20 rounded-3xl bg-[#F77F11] text-white p-8 sm:p-12 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
          <div>
            <p className="text-white/80 text-xs tracking-[0.22em] font-semibold uppercase">
              {isEs ? 'El primer paso es gratis' : 'The first step is free'}
            </p>
            <h3 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight max-w-xl tracking-[-0.015em]">
              {isEs
                ? '15 minutos. Sin compromiso. Empecemos por entenderte.'
                : "15 minutes. No commitment. Let's start by understanding you."}
            </h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={`https://wa.me/14085901102?text=${encodeURIComponent(isEs ? 'Hola Kitxens, quiero una llamada de descubrimiento de 15 minutos.' : "Hi Kitxens, I'd like a 15-minute discovery call.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 bg-white text-[#F77F11] hover:bg-neutral-100 h-12 px-6 rounded-full text-sm font-semibold"
              data-testid="how-cta-whatsapp"
            >
              <MessageCircle className="w-4 h-4" /> {isEs ? 'Agendar por WhatsApp' : 'Book on WhatsApp'}
            </a>
            <Link to="/contact#contact-form" className="inline-flex items-center gap-1 bg-white/0 border border-white/40 hover:bg-white/10 text-white h-12 px-6 rounded-full text-sm font-semibold" data-testid="how-cta-talk">
              {isEs ? 'Hablar con un experto' : 'Talk to an expert'} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Quick contact */}
        <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-neutral-600">
          <a href="https://wa.me/14085901102" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-[#F77F11]"><MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp <span className="text-[11px] text-neutral-400">(ESP | ENG)</span></a>
          <a href="tel:+14155982310" className="inline-flex items-center gap-2 hover:text-[#F77F11]"><Phone className="w-4 h-4 text-[#F77F11]" /> +1 (415) 598-2310</a>
          <a href="mailto:hello@kitxens.com" className="inline-flex items-center gap-2 hover:text-[#F77F11]"><Mail className="w-4 h-4 text-[#F77F11]" /> hello@kitxens.com</a>
        </div>
      </div>
      <ContinueExploring />
      <InternalLinkFooter />
    </div>
  );
}
